/** A supplement to an approved report: what happened since, a case-status change, people and property added. */
import { useEffect, useRef, useState } from 'react';
import { type IncidentReport, type ReportIssue, type Supplement } from '@civilos/domain';
import { api, ApiError } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, ErrorBox, Notice, Spinner, useAction } from '../shared/ui.tsx';
import { PeopleStep, PropertyStep, reportsBase } from './ReportWizard.tsx';

const STATUS: Record<string, [string, 'neutral' | 'info' | 'warn' | 'ok']> = {
  draft: ['Draft', 'neutral'],
  submitted: ['Waiting for approval', 'info'],
  returned: ['Sent back to fix', 'warn'],
  approved: ['Approved', 'ok'],
};
const CLEARANCE: [string, string][] = [
  ['', 'No change'],
  ['open', 'Open / under investigation'],
  ['arrest', 'Cleared by arrest'],
  ['exceptional', 'Cleared exceptionally'],
  ['closed', 'Closed, not cleared (administrative)'],
  ['unfounded', 'Unfounded'],
];

export function SupplementEditor({ id }: { id: string }) {
  const { navigate } = useRouter();
  const [data, setData] = useState<any>(null);
  const [body, setBody] = useState<Supplement | null>(null);
  const [issues, setIssues] = useState<ReportIssue[]>([]);
  const [save, setSave] = useState<'saved' | 'saving' | 'dirty' | 'conflict' | 'error'>('saved');
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [tab, setTab] = useState<'narrative' | 'people' | 'property'>('narrative');
  const version = useRef(0);
  const pending = useRef<Supplement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const busy = useRef(false);
  const a = useAction();
  const [note, setNote] = useState('');

  const load = () =>
    api
      .get<any>(`/api/supplements/${id}`)
      .then((d) => {
        setData(d);
        setBody(d.content);
        setIssues(d.issues);
        version.current = d.supplement.version;
        setSave('saved');
      })
      .catch(setLoadErr);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const flush = async () => {
    if (busy.current || !pending.current) return;
    busy.current = true;
    const b = pending.current;
    pending.current = null;
    setSave('saving');
    try {
      const r = await api.post<{ version: number; issues: ReportIssue[] }>(`/api/supplements/${id}/save`, { body: b, version: version.current });
      version.current = r.version;
      setIssues(r.issues);
      setSave(pending.current ? 'dirty' : 'saved');
    } catch (e) {
      setSave(e instanceof ApiError && e.status === 409 ? 'conflict' : 'error');
      if (!(e instanceof ApiError)) pending.current = pending.current ?? b;
    } finally {
      busy.current = false;
      if (pending.current) timer.current = setTimeout(flush, 600);
    }
  };
  useEffect(() => () => void flush(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (f: (b: Supplement) => void) => {
    setBody((cur) => {
      const next = structuredClone(cur!);
      f(next);
      pending.current = next;
      return next;
    });
    setSave('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };

  if (loadErr) return <ErrorBox error={loadErr} />;
  if (!data || !body) return <Spinner />;
  const current: IncidentReport = data.current;
  // The people / property steps work on a report; give them one made of this supplement's additions.
  const pseudo: IncidentReport = { ...current, people: body.people, property: body.property };
  const setPseudo = (f: (r: IncidentReport) => void) =>
    update((b) => {
      const r = { ...pseudo, people: b.people, property: b.property };
      f(r);
      b.people = r.people;
      b.property = r.property;
    });
  const errors = issues.filter((i) => i.level === 'error');
  const [label, tone] = STATUS[data.supplement.status] ?? [data.supplement.status, 'neutral'];
  const editable = data.canEdit;
  const back = () => (void flush(), navigate(`${reportsBase()}/${data.supplement.incidentId}`));
  const act = (path: string, payload?: object) =>
    a.run(async () => {
      await flush();
      await api.post(`/api/supplements/${id}/${path}`, payload ?? {});
      navigate(`${reportsBase()}/${data.supplement.incidentId}`);
    });
  return (
    <div className="rw rec">
      <header className="rec-head">
        <div className="crumb">
          <button type="button" className="btn link" onClick={back}>
            Incident {data.supplement.caseNumber}
          </button>{' '}
          ›
        </div>
        <div className="rec-title">
          <h1>
            <span className="mono">{data.supplement.caseNumber}</span> <span className="rec-sep">—</span> Supplement {data.supplement.number}
          </h1>
          <Badge tone={tone} dot>
            {label}
          </Badge>
          {editable && <span className={`rw-save ${save}`}>{save === 'saved' ? '✓ Saved' : save === 'saving' ? 'Saving…' : save === 'dirty' ? 'Editing…' : save === 'conflict' ? 'Changed elsewhere — reload' : 'Not saved — will retry'}</span>}
        </div>
        <dl className="rec-meta">
          <div>
            <dt>Written by</dt>
            <dd>{data.supplement.authorName}</dd>
          </div>
          <div>
            <dt>Case status now</dt>
            <dd>{CLEARANCE.find((c) => c[0] === current.clearance.status)?.[1] ?? '—'}</dd>
          </div>
          <div>
            <dt>Adds</dt>
            <dd>
              {body.people.length} people · {body.property.length} property
            </dd>
          </div>
        </dl>
      </header>
      {data.supplement.status === 'returned' && data.supplement.returnNote && (
        <Notice tone="warn">
          <strong>Returned by your supervisor:</strong> {data.supplement.returnNote}
        </Notice>
      )}
      <div className="rec-grid two">
      <nav className="rw-steps rec-secs" aria-label="Supplement parts">
        {(
          [
            ['narrative', 'What happened since'],
            ['people', `People added (${body.people.length})`],
            ['property', `Property added (${body.property.length})`],
          ] as const
        ).map(([k, l], i) => (
          <button key={k} type="button" className={tab === k ? 'on' : ''} onClick={() => (void flush(), setTab(k))}>
            <span className="n">{i + 1}</span>
            <span className="l">{l}</span>
          </button>
        ))}
      </nav>
      <div className="rec-main">
      <fieldset className="rw-body" disabled={!editable}>
        {tab === 'narrative' && (
          <div className="stack">
            <section className="rw-card">
              <header>
                <h3>What happened since the report</h3>
              </header>
              <textarea className="rw-narr" aria-label="Supplement narrative" value={body.narrative} onChange={(e) => update((b) => void (b.narrative = e.target.value))} placeholder="Follow-up interviews, evidence results, property recovered, an arrest…" />
            </section>
            <section className="rw-card">
              <header>
                <h3>Case status</h3>
                <span className="small muted">Now: {CLEARANCE.find((c) => c[0] === current.clearance.status)?.[1]}</span>
              </header>
              <div className="rw-choices" role="radiogroup" aria-label="Case status">
                {CLEARANCE.map(([v, l]) => {
                  const on = (body.clearance?.status ?? '') === v;
                  return (
                    <button key={v || 'none'} type="button" role="radio" aria-checked={on} className={on ? 'on' : ''} onClick={() => update((b) => void (b.clearance = v ? { status: v as IncidentReport['clearance']['status'], reason: null, date: null } : null))}>
                      {l}
                    </button>
                  );
                })}
              </div>
              {body.clearance?.status === 'exceptional' && (
                <div className="rw-row">
                  <label className="rw-f">
                    <span>Why</span>
                    <select value={body.clearance.reason ?? ''} onChange={(e) => update((b) => void (b.clearance!.reason = e.target.value || null))}>
                      <option value="">Choose…</option>
                      <option value="A">Death of the offender</option>
                      <option value="B">Prosecution declined</option>
                      <option value="C">In custody of another jurisdiction</option>
                      <option value="D">Victim refused to cooperate</option>
                      <option value="E">Juvenile, no custody</option>
                    </select>
                  </label>
                  <label className="rw-f">
                    <span>Date cleared</span>
                    <input type="date" value={body.clearance.date ?? ''} onChange={(e) => update((b) => void (b.clearance!.date = e.target.value || null))} />
                  </label>
                </div>
              )}
            </section>
          </div>
        )}
        {tab === 'people' && <PeopleStep r={pseudo} set={setPseudo} reportId={data.supplement.incidentId} />}
        {tab === 'property' && <PropertyStep r={pseudo} set={setPseudo} hideNeeds />}
      </fieldset>

      {(editable || data.canSubmit) && errors.length > 0 && (
        <section className="rw-card rw-fix">
          <header>
            <h3>
              {errors.length} thing{errors.length === 1 ? '' : 's'} to finish
            </h3>
          </header>
          <ul className="rw-issues">
            {errors.map((i, k) => (
              <li key={k}>
                <span>{i.message}</span>
                <button type="button" className="btn sm" onClick={() => setTab(i.step === 'people' ? 'people' : i.step === 'property' ? 'property' : 'narrative')}>
                  Fix
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="rw-actions">
        {data.canSubmit && (
          <button type="button" className="btn primary big" disabled={a.busy || errors.length > 0} onClick={() => act('submit')}>
            Send for approval
          </button>
        )}
        {data.canDiscard && (
          <button type="button" className="btn ghost" disabled={a.busy} onClick={() => window.confirm('Throw away this supplement?') && act('discard')}>
            Throw away
          </button>
        )}
      </div>
      {data.canReview && (
        <section className="rw-card rw-reviewbox">
          <header>
            <h3>Supervisor review</h3>
          </header>
          <button type="button" className="btn primary big" disabled={a.busy || errors.length > 0} onClick={() => act('approve')}>
            Approve supplement
          </button>
          <label className="rw-f wide">
            <span>Or send it back — what needs fixing?</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="button" className="btn" disabled={a.busy || note.trim().length < 3} onClick={() => act('return', { note: note.trim() })}>
            Send back to {data.supplement.authorName}
          </button>
        </section>
      )}
      <ErrorBox error={a.error} />
      </div>
      </div>
    </div>
  );
}
